/**
 * Pure helpers for skin results: colors, score bands, AQI/UV, and detection drawing.
 */

import { normalizeConditionName } from './skinConditionNormalization';

const bgrToRgb = (b, g, r) => `rgb(${r}, ${g}, ${b})`;

const BGR_COLORS_BY_CLASS_ID = {
  0: [255, 191, 0], 1: [255, 190, 0], 2: [255, 190, 0], 3: [255, 0, 255],
  4: [0, 0, 255], 5: [60, 20, 220], 6: [0, 69, 255], 7: [128, 128, 128],
  8: [100, 100, 100], 9: [180, 105, 255], 10: [211, 0, 148], 11: [255, 0, 255],
  12: [200, 200, 0], 13: [225, 105, 65], 14: [240, 32, 160], 15: [214, 112, 218],
  16: [180, 180, 180],
};

export function getColorByClassId(classId) {
  const bgr = BGR_COLORS_BY_CLASS_ID[classId];
  if (bgr) {
    const [b, g, r] = bgr;
    return bgrToRgb(b, g, r);
  }
  return 'rgb(160, 32, 240)';
}

export const CLASS_COLORS_MAP = {
  'Wrinkles': 'rgb(0, 191, 255)', 'Dry skin': 'rgb(0, 190, 255)', 'Oily skin': 'rgb(0, 190, 255)',
  'Dark circle': 'rgb(255, 0, 255)', 'Acne': 'rgb(255, 0, 0)', 'Red spot': 'rgb(220, 20, 60)',
  'Pigmentation': 'rgb(255, 69, 0)', 'Unknown': 'rgb(128, 128, 128)', 'Mole': 'rgb(100, 100, 100)',
  'Dark spots': 'rgb(255, 105, 180)', 'Whiteheads': 'rgb(148, 0, 211)', "Crow's feet": 'rgb(255, 0, 255)',
  'Open Pores': 'rgb(0, 200, 200)', 'Blackheads': 'rgb(65, 105, 225)', 'Clogged Pores': 'rgb(160, 32, 240)',
  'Eye Bags': 'rgb(218, 112, 214)', 'Hydration': 'rgb(0, 200, 120)', 'Oxygen': 'rgb(100, 149, 237)',
  'Smoothness': 'rgb(218, 112, 214)', 'Dullness': 'rgb(205, 133, 0)', 'UnevenSkin': 'rgb(255, 99, 71)', 'Uneven Skintone': 'rgb(255, 99, 71)',
  'Normal skin': 'rgb(180, 180, 180)',
};

export function getScoreColor(score) {
  if (score >= 0 && score <= 39) return '#ef4444';
  if (score >= 40 && score <= 59) return '#f97316';
  if (score >= 60 && score <= 74) return '#eab308';
  if (score >= 75 && score <= 89) return '#84cc16';
  return '#22c55e';
}

const CONDITION_GRADIENTS = {
  Oxygen: { dark: [0, 0, 139], light: [180, 220, 255] },
  Smoothness: { dark: [130, 0, 100], light: [255, 200, 255] },
  Dullness: { dark: [150, 50, 0], light: [255, 200, 0] },
  UnevenSkin: { dark: [200, 50, 0], light: [200, 220, 255] },
  'Uneven Skintone': { dark: [200, 50, 0], light: [200, 220, 255] },
  Hydration: { dark: [48, 18, 59], light: [255, 255, 39] },
};

export function getConditionGradient(conditionName) {
  const canonical = canonicalSkinConditionName(conditionName) || conditionName;
  return CONDITION_GRADIENTS[canonical] || CONDITION_GRADIENTS[conditionName] || { dark: [0, 0, 0], light: [255, 255, 255] };
}

export const EYE_CONDITIONS_ONE_PER_EYE = ['Dark circle', 'Eye Bags'];

export const SKIN_CONDITION_NAMES = ['Hydration', 'Oxygen', 'Smoothness', 'Dullness', 'UnevenSkin'];

const SKIN_CONDITION_ALIAS_MAP = {
  hydration: 'Hydration',
  oxygen: 'Oxygen',
  oxygenation: 'Oxygen',
  smoothness: 'Smoothness',
  dullness: 'Dullness',
  uneven_skintone: 'UnevenSkin',
  unevenskintone: 'UnevenSkin',
  'uneven skintone': 'UnevenSkin',
  'uneven skin': 'UnevenSkin',
  unevenskin: 'UnevenSkin',
  'skin tone evenness': 'UnevenSkin',
  'tone evenness': 'UnevenSkin',
};

function simplifySkinConditionKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Map API / label-score aliases to canonical skin-quality names. */
export function canonicalSkinConditionName(name) {
  if (!name || typeof name !== 'string') return null;
  const normalized = normalizeConditionName(name);
  const simplified = simplifySkinConditionKey(normalized);
  const compact = simplified.replace(/\s+/g, '');
  const alias = SKIN_CONDITION_ALIAS_MAP[simplified] || SKIN_CONDITION_ALIAS_MAP[compact];
  if (alias) return alias;
  if (SKIN_CONDITION_NAMES.includes(normalized)) return normalized;
  const trimmed = String(name).trim();
  if (SKIN_CONDITION_NAMES.includes(trimmed)) return trimmed;
  return null;
}

export function isSkinConditionName(name) {
  return Boolean(canonicalSkinConditionName(name));
}

/** Severity bar gradient CSS for hydration, oxygen, dullness, smoothness, uneven tone. */
export function buildSeverityBarGradient(conditionName) {
  const canonical = canonicalSkinConditionName(conditionName);
  if (!canonical) return null;

  if (canonical === 'Hydration') {
    return [
      'linear-gradient(to right',
      'rgb(212, 44, 42) 0%',
      'rgb(254, 125, 21) 12%',
      'rgb(254, 197, 24) 24%',
      'rgb(161, 218, 55) 38%',
      'rgb(32, 181, 141) 52%',
      'rgb(44, 127, 184) 66%',
      'rgb(72, 57, 166) 80%',
      'rgb(48, 18, 59) 100%)',
    ].join(', ');
  }

  if (canonical === 'UnevenSkin') {
    return [
      'linear-gradient(to right',
      'rgb(212, 44, 42) 0%',
      'rgb(254, 125, 21) 16%',
      'rgb(254, 197, 24) 32%',
      'rgb(32, 181, 141) 56%',
      'rgb(44, 127, 184) 74%',
      'rgb(72, 57, 166) 88%',
      'rgb(48, 18, 59) 100%)',
    ].join(', ');
  }

  const gradient = getConditionGradient(canonical);
  return `linear-gradient(to right, rgb(${gradient.dark[0]}, ${gradient.dark[1]}, ${gradient.dark[2]}) 0%, rgb(${gradient.light[0]}, ${gradient.light[1]}, ${gradient.light[2]}) 100%)`;
}

/** Flip horizontal severity gradients for the mobile vertical bar layout. */
export function toVerticalSeverityGradient(gradientCss) {
  if (!gradientCss) return null;
  return gradientCss.replace(/\bto right\b/gi, 'to bottom');
}

export function buildSeverityBarGradientStyleVars(conditionName) {
  const horizontal = buildSeverityBarGradient(conditionName);
  if (!horizontal) return null;
  const vertical = toVerticalSeverityGradient(horizontal);
  return {
    '--gradient-bg': horizontal,
    '--gradient-bg-vertical': vertical,
  };
}

/**
 * For under-eye conditions: at most one box per eye (left/right). Others: return as-is.
 */
export function getDetectionsToDraw(conditionName, filteredDetections, imageWidth) {
  if (!EYE_CONDITIONS_ONE_PER_EYE.includes(conditionName) || !filteredDetections.length || imageWidth == null) {
    return filteredDetections;
  }
  const midX = imageWidth / 2;
  const left = [];
  const right = [];
  filteredDetections.forEach((d) => {
    const bb = d.bbox || [];
    if (bb.length === 4) {
      const cx = (bb[0] + bb[2]) / 2;
      if (cx < midX) left.push(d); else right.push(d);
    }
  });
  const mergeSide = (list) => {
    if (list.length === 0) return null;
    if (list.length === 1) return list[0];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let best = list[0];
    list.forEach((d) => {
      const bb = d.bbox || [];
      if (bb.length === 4) {
        minX = Math.min(minX, bb[0]); minY = Math.min(minY, bb[1]);
        maxX = Math.max(maxX, bb[2]); maxY = Math.max(maxY, bb[3]);
      }
      if ((d.confidence ?? 0) > (best.confidence ?? 0)) best = d;
    });
    return minX === Infinity ? list[0] : { ...best, bbox: [minX, minY, maxX, maxY] };
  };
  const out = [];
  if (left.length) out.push(mergeSide(left));
  if (right.length) out.push(mergeSide(right));
  return out.filter(Boolean);
}

export function getUvIndexColor(uvIndex) {
  if (uvIndex <= 2) return '#00E400';
  if (uvIndex <= 5) return '#FFFF00';
  if (uvIndex <= 7) return '#FF7E00';
  if (uvIndex <= 10) return '#FF0000';
  return '#8F3F97';
}

export function getAqiDescription(aqi) {
  if (aqi == null || aqi === undefined) return 'N/A';
  const n = Number(aqi);
  if (n <= 50) return 'Good';
  if (n <= 100) return 'Moderate';
  if (n <= 150) return 'Unhealthy for Sensitive';
  if (n <= 200) return 'Unhealthy';
  if (n <= 300) return 'Very Unhealthy';
  return 'Hazardous';
}

export function getUvDescription(uvIndex) {
  if (uvIndex == null || uvIndex === undefined) return 'N/A';
  const n = Number(uvIndex);
  if (n <= 2) return 'Low';
  if (n <= 5) return 'Moderate';
  if (n <= 7) return 'High';
  if (n <= 10) return 'Very High';
  return 'Extreme';
}

export function getAqiColor(aqi) {
  if (aqi == null || aqi === undefined) return '#64748b';
  const n = Number(aqi);
  if (n <= 50) return '#00E400';
  if (n <= 100) return '#FFFF00';
  if (n <= 150) return '#FF7E00';
  if (n <= 200) return '#FF0000';
  if (n <= 300) return '#8F3F97';
  return '#7E0023';
}
